/* Piezas compartidas de las pantallas de Control de EPP. */
import { IonButton, useIonToast } from "@ionic/react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { useCallback, useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import EppAPI, {
  DepositoRef,
  EntregaDetalle,
  EstadoEpp,
  Funcionario,
  etiquetaItem,
  fechaCorta,
  mensajeError,
  RolEpp,
} from "src/services/epp";
import "src/pages/Epp.css";

/** Rol del usuario en el módulo (cacheado en la sesión). null = cargando. */
export const useRolEpp = () => {
  const [rol, setRol] = useState<RolEpp | null>(null);
  useEffect(() => {
    let activo = true;
    EppAPI.miRol().then((r) => activo && setRol(r));
    return () => {
      activo = false;
    };
  }, []);
  return rol;
};

/** Saca de la ruta a quien no tiene permiso (no hace nada mientras carga el rol). */
export const GuardEpp: React.FC<{ permitir: (rol: RolEpp) => boolean }> = ({ permitir }) => {
  const rol = useRolEpp();
  if (rol && !permitir(rol)) return <Redirect to="/" />;
  return null;
};

export const useToastEpp = () => {
  const [present] = useIonToast();
  const ok = useCallback(
    (message: string) => present({ message, duration: 2500, color: "success" }),
    [present]
  );
  const error = useCallback(
    (err: any, defecto: string) => present({ message: mensajeError(err, defecto), duration: 4500, color: "danger" }),
    [present]
  );
  const aviso = useCallback(
    (message: string) => present({ message, duration: 3500, color: "warning" }),
    [present]
  );
  return { ok, error, aviso };
};

export const EstadoChip: React.FC<{ estado: EstadoEpp; etiqueta: string }> = ({ estado, etiqueta }) => (
  <span className={`epp-estado ${estado}`}>{etiqueta}</span>
);

/** Marca de retiro anticipado (antes de la fecha estimada de la entrega anterior).
 * Si el estado ya es RETIRO_ANTICIPADO solo se muestra el motivo; si el EPP ya está
 * por vencer o vencido, la marca conserva el dato de que fue anticipado. */
export const AnticipadaInfo: React.FC<{ motivo: string | null; estado: EstadoEpp }> = ({ motivo, estado }) => (
  <div className="epp-meta">
    {estado !== "RETIRO_ANTICIPADO" && <span className="epp-estado ANTICIPADA">RETIRO ANTICIPADO</span>}
    {motivo && <span>Motivo: {motivo}</span>}
  </div>
);

export const Cargando: React.FC = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
    <CircularProgress />
  </div>
);

export const SelectorDeposito: React.FC<{
  depositos: DepositoRef[];
  valor: number | "";
  onChange: (id: number) => void;
  label?: string;
}> = ({ depositos, valor, onChange, label = "Depósito" }) => (
  <TextField
    select
    label={label}
    variant="outlined"
    size="small"
    value={valor}
    onChange={(e) => onChange(Number(e.target.value))}
    style={{ minWidth: 220, flex: "1 1 220px" }}
  >
    {depositos.map((d) => (
      <MenuItem key={d.Id} value={d.Id}>
        {d.Nombre}
      </MenuItem>
    ))}
  </TextField>
);

/** Búsqueda de funcionarios activos por nombre, cédula o legajo (DWH). */
export const BuscadorFuncionario: React.FC<{
  label?: string;
  onSelect: (f: Funcionario | null) => void;
  valor?: Funcionario | null;
}> = ({ label = "Colaborador (nombre, cédula o legajo)", onSelect, valor = null }) => {
  const [texto, setTexto] = useState("");
  const [opciones, setOpciones] = useState<Funcionario[]>([]);
  const [buscando, setBuscando] = useState(false);
  const pedido = useRef(0);

  useEffect(() => {
    const q = texto.trim();
    if (q.length < 3 && !/^\d+$/.test(q)) {
      setOpciones([]);
      return;
    }
    const n = ++pedido.current;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await EppAPI.buscarFuncionarios(q);
        if (n === pedido.current) setOpciones(res);
      } catch {
        if (n === pedido.current) setOpciones([]);
      } finally {
        if (n === pedido.current) setBuscando(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [texto]);

  return (
    <MuiAutocomplete
      options={opciones}
      value={valor}
      filterOptions={(x) => x}
      getOptionLabel={(f) => `${f.nombre} · ${f.cedula}`}
      getOptionSelected={(a, b) => a.cedula === b.cedula}
      // "reset": MUI pone en el campo la etiqueta del elegido; no es una búsqueda.
      onInputChange={(_, v, motivo) => motivo !== "reset" && setTexto(v)}
      onChange={(_, f) => onSelect(f)}
      loading={buscando}
      noOptionsText={texto.trim() ? "Sin coincidencias" : "Escribí para buscar"}
      renderOption={(f) => (
        <div>
          <div style={{ fontWeight: 600 }}>{f.nombre}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            CI {f.cedula}
            {f.legajo ? ` · Legajo ${f.legajo}` : ""} · {f.cargo}
          </div>
        </div>
      )}
      renderInput={(params) => <TextField {...params} label={label} variant="outlined" size="small" fullWidth />}
    />
  );
};

/** Detalle de una entrega; el admin puede anularla. */
export const DetalleEntregaDialog: React.FC<{
  entregaId: number | null;
  puedeAnular?: boolean;
  onClose: () => void;
  onAnulada?: () => void;
}> = ({ entregaId, puedeAnular = false, onClose, onAnulada }) => {
  const toast = useToastEpp();
  const [entrega, setEntrega] = useState<EntregaDetalle | null>(null);
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setEntrega(null);
    setAnulando(false);
    setMotivo("");
    if (!entregaId) return;
    EppAPI.entrega(entregaId)
      .then(setEntrega)
      .catch((err) => {
        toast.error(err, "No se pudo abrir la entrega");
        onClose();
      });
  }, [entregaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const anular = async () => {
    if (!entrega || !motivo.trim()) return;
    setGuardando(true);
    try {
      await EppAPI.anularEntrega(entrega.Id, motivo.trim());
      toast.ok("Entrega anulada: el stock volvió al depósito");
      onAnulada?.();
      onClose();
    } catch (err) {
      toast.error(err, "No se pudo anular la entrega");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog className="epp-dialogo" open={entregaId !== null} onClose={() => !guardando && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Entrega #{entregaId}</DialogTitle>
      <DialogContent className="campos-mui">
        {!entrega ? (
          <Cargando />
        ) : (
          <>
            <div className="epp-card-titulo">{entrega.Nombre}</div>
            <div className="epp-meta">
              <span>CI {entrega.Cedula}</span>
              {entrega.Legajo && <span>Legajo {entrega.Legajo}</span>}
              {entrega.Cargo && <span>{entrega.Cargo}</span>}
              {entrega.Departamento && <span>{entrega.Departamento}</span>}
            </div>
            <div className="epp-meta">
              <span>{fechaCorta(entrega.Fecha)} {entrega.Fecha.slice(11, 16)}</span>
              <span>{entrega.Deposito}</span>
              <span>Entregó: {entrega.EntregadoPorNombre}</span>
            </div>
            {entrega.AnuladaFecha && (
              <div className="epp-aviso alerta">
                Anulada el {fechaCorta(entrega.AnuladaFecha)} por {entrega.AnuladaPorNombre}: {entrega.AnuladaMotivo}
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              {entrega.Lineas.map((l) => (
                <div key={l.Id} className="epp-fila">
                  <div className="epp-fila-cabecera">
                    <span className="epp-item">
                      {l.Cantidad} × {etiquetaItem(l.Item, l.Talle)}
                    </span>
                    <EstadoChip estado={l.Estado} etiqueta={l.EstadoEtiqueta} />
                  </div>
                  <div className="epp-meta">
                    <span>Reposición estimada: {fechaCorta(l.FechaReposicion)}</span>
                  </div>
                  {l.Anticipada && <AnticipadaInfo motivo={l.Motivo} estado={l.Estado} />}
                </div>
              ))}
            </div>
            {entrega.Observacion && <div className="epp-meta">Observación: {entrega.Observacion}</div>}
            {anulando && (
              <TextField
                label="Motivo de la anulación"
                variant="outlined"
                fullWidth
                margin="dense"
                multiline
                value={motivo}
                inputProps={{ maxLength: 300 }}
                onChange={(e) => setMotivo(e.target.value)}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {puedeAnular && entrega && !entrega.AnuladaFecha && (
          anulando ? (
            <IonButton color="danger" onClick={anular} disabled={guardando || !motivo.trim()}>
              {guardando ? "Anulando..." : "Confirmar anulación"}
            </IonButton>
          ) : (
            <IonButton fill="clear" color="danger" onClick={() => setAnulando(true)}>
              Anular
            </IonButton>
          )
        )}
        <IonButton fill="clear" onClick={onClose} disabled={guardando}>
          Cerrar
        </IonButton>
      </DialogActions>
    </Dialog>
  );
};
